"use client";

import { useCallback } from "react";
import { validateFields } from "@/lib/utils";
import { FormState, Section, VariantConfig, CategoryItem, NestedItem, CategoryItemChild } from "../types";
import {
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
  mapFieldsConfigToFormConfig,
  extractCategoriesFromResponse,
  getAdminApiFailureMessage,
  isAdminApiEnvelopeSuccess,
  prepareSectionPreviewData,
} from "../utils";

interface UseItemActionsProps {
  selectedSection: Section;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  pageComponentId?: string;
  variantConfig: VariantConfig | null;
  itemFieldsConfig: any[];
  currentUserId?: string | number;
  /** Visionaries: draft toggle may lag componentFields until save; mirror Form3 redux + draft. */
  reduxState?: Record<string, unknown> | null;
  onSave: (section: Section) => void;
  fetchData: (forceFetch?: boolean) => Promise<void>;
  ensureComponentParent?: () => Promise<number | null>;
}

// ============================================================================
// Helper: Build item fields from config
// ============================================================================
const buildItemFields = (
  itemFieldsConfig: any[],
  item?: CategoryItemChild
): Record<string, any> => {
  return itemFieldsConfig.reduce((acc, field) => {
    if (field.filedType === "TEXT_BLOCK") return acc;
    return {
      ...acc,
      [field.fieldName]: item?.[field.fieldName] ?? field.value ?? "",
    };
  }, {});
};

// ============================================================================
// Helper: Create form reset state
// ============================================================================
const createResetState = (defaults: Record<string, any>): Partial<FormState> => ({
  formFields: defaults,
  selectedItemType: null,
  selectedItemId: null,
  editingItemId: null,
  formFieldErrors: {},
  formFieldMessages: {},
});

// ============================================================================
// Main Hook
// ============================================================================
export const useItemActions = ({
  selectedSection,
  formState,
  setFormState,
  pageComponentId,
  variantConfig,
  itemFieldsConfig,
  currentUserId,
  reduxState,
  onSave,
  fetchData,
  ensureComponentParent,
}: UseItemActionsProps) => {
  const createItemMethod = (variantConfig as any)?.createItemMethod;
  const updateItemMethod = (variantConfig as any)?.updateItemMethod;
  const deleteItemMethod = (variantConfig as any)?.deleteItemMethod;
  const fetchCategoriesMethod = (variantConfig as any)?.fetchCategoriesMethod;
  const fetchNestedItemsMethod = (variantConfig as any)?.fetchNestedItemsMethod;
  const fetchItemsMethod = (variantConfig as any)?.fetchItemsMethod;
  const itemParentIdFieldName = (variantConfig as any)?.itemParentIdFieldName || "category_id";
  const childItemsFieldName = (variantConfig as any)?.childItemsFieldName || "categories";
  const supportsOptionalCategories =
    (variantConfig as any)?.supportsOptionalCategoriesToggle === true;
  const useCategoriesOff =
    formState.componentFields?.use_categories === false ||
    formState.componentFields?.use_categories === 0 ||
    reduxState?.use_categories === false ||
    reduxState?.use_categories === 0;
  const visionariesPoolOnlyItems = (variantConfig as any)?.visionariesPoolOnlyItems === true;
  const optionalDirectCardsMode =
    visionariesPoolOnlyItems || (supportsOptionalCategories && useCategoriesOff);
  const optionalCategoriesDirectParentKey = (variantConfig as any)
    ?.optionalCategoriesDirectParentKey as string | undefined;
  const optionalCategoriesParentIdErrorMessage =
    ((variantConfig as any)?.optionalCategoriesParentIdErrorMessage as string | undefined) ||
    "Save the component settings above before adding items.";

  // Three-level structure check
  const isThreeLevelStructure = Boolean(fetchNestedItemsMethod);

  // ---------------------------------------------------------------------------
  // Helper: Get Parent ID for item
  // ---------------------------------------------------------------------------
  const getItemParentId = (): { parentId: number | null; error: string | null } => {
    if (isThreeLevelStructure) {
      return formState.selectedNestedId
        ? { parentId: formState.selectedNestedId, error: null }
        : { parentId: null, error: "Please select a semester first" };
    }
    if (optionalDirectCardsMode) {
      return formState.parentId
        ? { parentId: formState.parentId, error: null }
        : { parentId: null, error: optionalCategoriesParentIdErrorMessage };
    }
    return { parentId: formState.selectedCategoryId, error: null };
  };

  // ---------------------------------------------------------------------------
  // Helper: Fetch category with nested items (for three-level structures)
  // ---------------------------------------------------------------------------
  const fetchCategoryWithNestedItems = async (category: any) => {
    if (!fetchNestedItemsMethod || !fetchItemsMethod) return category;
    
    try {
      const nestedResponse = await fetchNestedItemsMethod(category.id);
      const semesters = nestedResponse?.data || nestedResponse || [];
      
      const semestersWithItems = await Promise.all(
        (Array.isArray(semesters) ? semesters : []).map(async (semester: any) => {
          try {
            const itemResponse = await fetchItemsMethod(semester.id);
            const items = itemResponse?.data || itemResponse || [];
            return { ...semester, subjects: Array.isArray(items) ? items : [], items: Array.isArray(items) ? items : [] };
          } catch {
            return { ...semester, subjects: [], items: [] };
          }
        })
      );
      
      return { ...category, semesters: semestersWithItems };
    } catch {
      return { ...category, semesters: [] };
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: Update Preview
  // ---------------------------------------------------------------------------
  const updatePreview = async (parentIdValue: number | null) => {
    if (!onSave || !fetchCategoriesMethod || !pageComponentId) return;

    try {
      const response = await fetchCategoriesMethod(Number(pageComponentId));
      const { categories: categoriesList, componentData } = extractCategoriesFromResponse(
        response,
        childItemsFieldName
      );

      // For three-level structures, fetch nested items for each category
      let fullCategories = categoriesList;
      if (isThreeLevelStructure && fetchNestedItemsMethod && fetchItemsMethod) {
        fullCategories = await Promise.all(
          categoriesList.map((cat: any) => fetchCategoryWithNestedItems(cat))
        );
      }

      const mergedData = { ...componentData, ...formState.componentFields };
      const id = parentIdValue || componentData.id || selectedSection.componentId || 0;

      onSave(
        prepareSectionPreviewData(selectedSection, id, { ...mergedData, id }, fullCategories, childItemsFieldName)
      );
    } catch (error) {
      console.error("Failed to fetch updated data for preview:", error);
    }
  };

  // ---------------------------------------------------------------------------
  // Item Click Handler
  // ---------------------------------------------------------------------------
  const handleItemClick = useCallback(
    (category: CategoryItem, item: CategoryItemChild, semester?: NestedItem) => {
      const itemFields = buildItemFields(itemFieldsConfig, item);

      setFormState((prev) => ({
        ...prev,
        selectedItemType: "item",
        selectedCategoryId: category.id,
        selectedNestedId: semester?.id || null,
        selectedItemId: item.id,
        editingItemId: String(item.id),
        formFields: itemFields,
        currentFieldsConfig: mapFieldsConfigToFormConfig(itemFieldsConfig),
        formFieldErrors: {},
        formFieldMessages: {},
      }));
    },
    [itemFieldsConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // New Item Handler
  // ---------------------------------------------------------------------------
  const handleNewItem = useCallback(
    (category: CategoryItem, semester?: NestedItem) => {
      const defaults = buildItemFields(itemFieldsConfig);

      setFormState((prev) => ({
        ...prev,
        selectedItemType: "item",
        selectedCategoryId: category.id,
        selectedNestedId: semester?.id || null,
        selectedItemId: null,
        editingItemId: null,
        formFields: { ...defaults },
        currentFieldsConfig: [...mapFieldsConfigToFormConfig(itemFieldsConfig)],
        formFieldErrors: {},
        formFieldMessages: {},
      }));
    },
    [itemFieldsConfig, setFormState]
  );

  // ---------------------------------------------------------------------------
  // Item Save Handler
  // ---------------------------------------------------------------------------
  const handleItemSave = useCallback(async () => {
    const directCards = optionalDirectCardsMode;

    if (!directCards && !formState.selectedCategoryId) {
      showErrorToast("Please select a category first");
      return;
    }

    if (!formState.parentId && ensureComponentParent) {
      const pid = await ensureComponentParent();
      if (pid == null) return;
    }

    const { errors, messages } = validateFields(formState.currentFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));

    if (Object.keys(errors).length > 0) {
      showValidationErrorToast();
      return;
    }

    // Get parent ID based on structure level
    const { parentId, error } = getItemParentId();
    if (error) {
      showErrorToast(error);
      return;
    }

    try {
      const { id, ...rawItemData } = formState.formFields;
      const textBlockNames = new Set(
        itemFieldsConfig.filter((f: any) => f.filedType === "TEXT_BLOCK").map((f: any) => f.fieldName),
      );
      const itemData = Object.fromEntries(
        Object.entries(rawItemData).filter(([key]) => !textBlockNames.has(key)),
      );
      const isUpdate = formState.editingItemId && !String(formState.editingItemId).startsWith("temp_");

      const directParentPayload =
        optionalCategoriesDirectParentKey && formState.parentId != null
          ? { [optionalCategoriesDirectParentKey]: formState.parentId }
          : { visionaries_id: formState.parentId ?? undefined };

      const payload = directCards
        ? {
            ...itemData,
            category_id: null,
            ...directParentPayload,
            ...(pageComponentId ? { component_id: Number(pageComponentId) } : {}),
            ...(isUpdate ? { updated_by: currentUserId } : { created_by: currentUserId }),
          }
        : {
            ...itemData,
            [itemParentIdFieldName]: parentId,
            ...(isUpdate ? { updated_by: currentUserId } : { created_by: currentUserId }),
          };

      // Save operation using object mapping
      const saveOperations = {
        update: {
          canExecute: () => isUpdate && updateItemMethod && formState.editingItemId,
          execute: async () => {
            const response = await updateItemMethod!(Number(formState.editingItemId), payload);
            if (!isAdminApiEnvelopeSuccess(response)) {
              showErrorToast(
                getAdminApiFailureMessage(
                  response,
                  "Failed to update item. Save the parent section first if it is new."
                )
              );
              return;
            }
            showSuccessToast("Item updated successfully");
          },
        },
        create: {
          canExecute: () => !isUpdate && createItemMethod,
          execute: async () => {
            const response = await createItemMethod!(payload);
            if (!isAdminApiEnvelopeSuccess(response)) {
              showErrorToast(
                getAdminApiFailureMessage(
                  response,
                  "Failed to create item. Save the parent section first if it is new."
                )
              );
              return;
            }
            const row = response.data as { id?: number } | undefined;
            const responseId = row?.id;
            responseId &&
              setFormState((prev) => ({
                ...prev,
                editingItemId: String(responseId),
                selectedItemId: responseId,
              }));
            showSuccessToast("Item created successfully");
          },
        },
      };

      const operation = Object.values(saveOperations).find((op) => op.canExecute());
      if (!operation) {
        showErrorToast("Could not save item. Ensure a category is selected and try again.");
        return;
      }
      await operation.execute();

      // Reset form and refresh
      const defaults = buildItemFields(itemFieldsConfig);
      setFormState((prev) => ({ ...prev, ...createResetState(defaults) }));
      await updatePreview(formState.parentId);
      await fetchData(true);
    } catch (error: any) {
      showErrorToast(error?.message || "Failed to save item");
    }
  }, [
    supportsOptionalCategories,
    useCategoriesOff,
    optionalDirectCardsMode,
    formState.selectedCategoryId,
    formState.selectedNestedId,
    formState.currentFieldsConfig,
    formState.formFields,
    formState.editingItemId,
    formState.parentId,
    formState.componentFields,
    ensureComponentParent,
    itemFieldsConfig,
    itemParentIdFieldName,
    currentUserId,
    isThreeLevelStructure,
    createItemMethod,
    updateItemMethod,
    fetchCategoriesMethod,
    fetchNestedItemsMethod,
    fetchItemsMethod,
    pageComponentId,
    selectedSection,
    onSave,
    fetchData,
    setFormState,
    variantConfig,
    optionalCategoriesDirectParentKey,
    optionalCategoriesParentIdErrorMessage,
  ]);

  // ---------------------------------------------------------------------------
  // Item Delete Handler
  // ---------------------------------------------------------------------------
  const handleItemDelete = useCallback(
    async (category: CategoryItem, item: CategoryItemChild, semester?: NestedItem) => {
      if (!deleteItemMethod) return;

      try {
        await deleteItemMethod(item.id);
        showSuccessToast("Subject deleted successfully");

        // Clear form if deleting selected item
        formState.selectedItemId === item.id &&
          setFormState((prev) => ({
            ...prev,
            selectedItemType: null,
            selectedItemId: null,
            selectedNestedId: null,
            editingItemId: null,
            formFields: {},
          }));

        await updatePreview(formState.parentId);
        await fetchData(true);
      } catch (error) {
        showErrorToast("Failed to delete item");
      }
    },
    [deleteItemMethod, formState.selectedItemId, formState.parentId, formState.componentFields, fetchCategoriesMethod, fetchNestedItemsMethod, fetchItemsMethod, isThreeLevelStructure, childItemsFieldName, pageComponentId, selectedSection, onSave, fetchData, setFormState]
  );

  return {
    handleItemClick,
    handleNewItem,
    handleItemSave,
    handleItemDelete,
  };
};
